import React, { useMemo } from "react";
import {
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";

const MainTable = ({
    // Core Data and Column Configuration
    data = [],
    columns = [],
    columnPinning = [],

    // Table Metadata and General Settings
    title = "",
    enableRowSelection = false,
    enableGlobalFilter = true,
    enableColumnFilters = true,
    enableSorting = true,
    enablePagination = true,
    paginationDisplayMode = "default",
    manualPaginationMode = false,

    // Display and Column Options
    displayColumnDefOptions,
    columnVisibility,

    // Filtering and Global Filter
    manualFiltering = false,
    onGlobalFilterChange,
    globalFilterValue = '',

    // Editing and Density Toggles
    enableEditing = false,
    enableDensityToggle = true,
    enableFullScreenToggle = true,

    // Column Behavior and Customization
    enableColumnResizing = true,
    enableHiding = true,
    enableGrouping = true,
    groupedColumnMode = 'reorder',

    // Sticky Headers and Footers
    enableStickyHeader = true,
    enableStickyFooter = true,

    // Row and Editing Behavior
    editDisplayMode = '',
    createDisplayMode = '',
    onCreatingRowSave = {},
    onRowClick,
    onSelectionChange,
    enableRowActions,
    renderRowActions,
    positionActionsColumn,
    renderEditRowDialogContent,
    renderTopToolbarCustomActions,
    renderCreateRowDialogContent,
    onEditingRowSave,

    // Pagination Settings
    rowsPerPageOptions = [10, 20, 50, 100],
    initialPageSize = 10,
    currentPage = 0,
    totalPages = 1,
    onPageChange = () => { },

    // Initial States
    initialGrouping = [],
    initialExpanded = {},
    isLoading = false,

    // Customization for the Table Container
    muiTableContainerProps = {},
}) => {
    const memoizedColumns = useMemo(
        () =>
            columns.map((column) => ({
                ...column,
                size: column.size || 220,
            })),
        [columns]
    );

    // Initialize the table using Material React Table's hooks
    const table = useMaterialReactTable({
        // Core Table Configuration
        columns: memoizedColumns,
        data,

        // Pagination Settings
        enablePagination,
        rowsPerPageOptions,
        paginationDisplayMode,
        positionPagination: "bottom",
        manualPagination: manualPaginationMode,
        pageCount: totalPages,
        state: {
            pagination: { pageIndex: currentPage },
            globalFilter: globalFilterValue,
            isSaving: isLoading,
        },
        onPaginationChange: (updater) => {
            const newPageIndex = updater instanceof Function ? updater(currentPage) : updater;
            onPageChange(newPageIndex);
        },

        // Global Filtering
        onGlobalFilterChange: (updater) => {
            const newValue = updater instanceof Function
                ? updater(globalFilterValue)
                : updater;
            onGlobalFilterChange(newValue);
        },
        enableGlobalFilter,
        manualFiltering,
        positionGlobalFilter: "right",

        // Editing and Creating Rows
        enableEditing,
        editDisplayMode,
        onEditingRowSave: async ({ row, table, values }) => {
            if (onEditingRowSave) {
                await onEditingRowSave(row, table, values);
            }
        },
        createDisplayMode,
        onCreatingRowSave,

        // Sorting and Column Features
        enableSorting,
        enableColumnFilters,
        enableColumnResizing,
        enableHiding,
        enableGrouping,
        groupedColumnMode,
        displayColumnDefOptions,

        // Sticky Header/Footer
        enableStickyHeader,
        enableStickyFooter,

        // Toolbar and Density Toggles
        enableTopToolbar: true,
        enableBottomToolbar: true,
        enableDensityToggle,
        enableFullScreenToggle,
        positionToolbarAlertBanner: "top",

        // Initial State
        initialState: {
            pagination: { pageSize: initialPageSize },
            columnPinning: columnPinning,
            density: "comfortable",
            showGroupingDropzone: true,
            grouping: initialGrouping,
            expanded: initialExpanded,
            columnVisibility: columnVisibility,
        },

        // Row and Action Configurations
        enableRowSelection,
        enableRowActions,
        onRowClick,
        onRowSelectionChange: onSelectionChange,
        renderRowActions,
        positionActionsColumn,
        renderTopToolbarCustomActions,
        renderEditRowDialogContent,
        renderCreateRowDialogContent,

        // Styling for the Table
        muiTablePaperProps: {
            elevation: 0,
            sx: {
                boxShadow: "none",
                border: '1px solid #0000001f',
                borderRadius: '10px',
            },
        },
        muiTableProps: {
            sx: {
                boxShadow: "none",
            },
        },
        muiTableContainerProps: {
            sx: {
                ...muiTableContainerProps.sx,
            },
        },
        muiTableHeadCellProps: {
            sx: {
                fontSize: "13px",
                padding: "0rem 0.5rem",
                paddingBottom: "0.5rem",
                paddingTop: "0.5rem",
                fontWeight: 500,
                backgroundColor: "var(--rgba-primary-1)",
                color: "var(--primary)",
                border: 0,
                verticalAlign: "middle",
            },
        },
        muiTableBodyCellProps: {
            sx: {
                '& .MuiStack-root': {
                    justifyContent: 'space-between',
                },
                border: '.5px solid rgba(81, 81, 81, .07)',
            },
        },
        muiPaginationProps: {
            color: '#ECE9F4',
            shape: 'rounded',
            variant: 'outlined',
            showRowsPerPage: false,
        },
        muiSearchTextFieldProps: {
            size: "small",
            variant: "outlined",
        },
    });


    return (
        <div className="w-full p-2">
            {title && <h4 className="text-xl font-semibold mb-4">{title}</h4>}
            <MaterialReactTable table={table} />
        </div>
    );
};

export default MainTable;
